// ManageCardsModal.jsx — see and remove a client's cards on file (owner
// request 2026-10-28). Lists every card from both our DB and the client's
// Stripe customer; removing detaches it in Stripe too, so the POS can't
// quietly bring it back. Opened from the "Card on File" row in the profile.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";

const C = {
  card:"#ffffff", border:"#e8e4df", goldBg:"rgba(180,140,60,0.08)", borderGold:"rgba(180,140,60,0.3)",
  gold:"#b48c3c", green:"#2e8b57", greenL:"rgba(46,139,87,0.12)", red:"#c0392b", redL:"rgba(192,57,43,0.08)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

async function callCards(action, body) {
  const { data, error } = await supabase.functions.invoke("stripe-payment", { body: { action, ...body } });
  let msg = data?.error || error?.message;
  try { if (error?.context?.json) { const j = await error.context.json(); msg = j?.error || msg; } } catch (_) {}
  if (error || data?.error) throw new Error(msg || "Unknown error");
  return data;
}

export default function ManageCardsModal({ client, onClose, onChanged }) {
  const [cards, setCards] = useState(null);
  const [membership, setMembership] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    setErr("");
    try {
      const d = await callCards("list_client_cards", { client_id: client.id });
      setCards(d.cards || []);
      setMembership(!!d.active_membership);
    } catch (e) { setErr(e.message); setCards([]); }
  };
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (card) => {
    const what = card ? `${(card.brand || "card").toUpperCase()} •••• ${card.last4}` : `ALL ${cards.length} cards`;
    const warn = membership ? "\n\n⚠️ She has an active membership — without a card the next monthly charge will fail." : "";
    if (!window.confirm(`Remove ${what} from ${client.name}?\n\nThe card is removed from Stripe too. No-show / late fees can't be charged until a new card is saved.${warn}`)) return;
    setBusy(card ? card.id : "all"); setErr(""); setNote("");
    try {
      const d = await callCards("remove_client_cards", { client_id: client.id, ...(card ? { payment_method_id: card.id } : {}) });
      if (d.failed?.length) setErr("Some cards were NOT removed: " + d.failed.map(f => f.error).join("; "));
      if (d.removed?.length) setNote("✅ Removed " + d.removed.join(", "));
      onChanged?.(d.remaining > 0);
      await load();
    } catch (e) { setErr(e.message); }
    setBusy("");
  };

  const exp = (c) => c.exp_month ? `${String(c.exp_month).padStart(2, "0")}/${String(c.exp_year).slice(-2)}` : "";

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:5000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(3px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.card,borderRadius:14,width:"100%",maxWidth:420,padding:22,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.35)",fontFamily:F}}>
        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:2}}>💳 Cards on File</div>
        <div style={{fontSize:12.5,color:C.muted,marginBottom:14}}>{client.name}</div>

        {cards === null && <div style={{fontSize:12.5,color:C.muted,padding:"14px 0"}}>Loading cards…</div>}
        {cards && cards.length === 0 && !err && (
          <div style={{padding:"16px",background:C.light,borderRadius:9,fontSize:12.5,color:C.muted,textAlign:"center"}}>No card on file.</div>
        )}
        {cards && cards.map(c => (
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 12px",border:`1px solid ${C.border}`,borderRadius:9,marginBottom:8}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13.5,fontWeight:700,color:C.text}}>{(c.brand || "Card").toUpperCase()} •••• {c.last4}</div>
              <div style={{fontSize:10.5,color:C.muted}}>{exp(c) && `Expires ${exp(c)}`}{c.is_default ? " · Default" : ""}</div>
            </div>
            <button onClick={()=>remove(c)} disabled={!!busy} style={{padding:"6px 12px",background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:7,fontSize:11.5,fontWeight:700,cursor:busy?"not-allowed":"pointer",fontFamily:F}}>
              {busy === c.id ? "Removing…" : "Remove"}
            </button>
          </div>
        ))}
        {cards && cards.length > 1 && (
          <button onClick={()=>remove(null)} disabled={!!busy} style={{width:"100%",padding:"10px",marginTop:4,background:"#fff",color:C.red,border:`1px solid ${C.red}60`,borderRadius:9,fontSize:12.5,fontWeight:700,cursor:busy?"not-allowed":"pointer",fontFamily:F}}>
            {busy === "all" ? "Removing…" : `Remove all ${cards.length} cards`}
          </button>
        )}
        {membership && cards?.length > 0 && (
          <div style={{marginTop:10,padding:"9px 11px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:8,fontSize:11.5,color:C.gold}}>
            Active membership — her monthly charge uses this card.
          </div>
        )}

        {note && <div style={{marginTop:10,padding:10,background:C.greenL,borderRadius:8,color:C.green,fontSize:12.5,fontWeight:600}}>{note}</div>}
        {err && <div style={{marginTop:10,padding:10,background:C.redL,border:`1px solid ${C.red}40`,borderRadius:8,color:C.red,fontSize:12.5}}>{err}</div>}

        <button onClick={onClose} style={{width:"100%",marginTop:14,padding:"10px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:9,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Close</button>
      </div>
    </div>
  );
}
